
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Calendar, CreditCard } from "lucide-react";
import { format } from "date-fns";
import { RefundHistory } from "@/hooks/useRefundHistory";

interface RefundHistorySectionProps {
  refunds: RefundHistory[];
  isLoading: boolean;
}

export const RefundHistorySection = ({ refunds, isLoading }: RefundHistorySectionProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <RefreshCw className="h-5 w-5 mr-2" />
            Refund History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!refunds || refunds.length === 0) {
    return null;
  }

  const formatRefundDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const isSameDay = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      
      if (isSameDay) {
        return `Today at ${format(date, 'h:mm a')}`;
      } else {
        return `${format(date, 'do MMMM yyyy')} at ${format(date, 'h:mm a')}`;
      }
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Processing</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-lg">
          <RefreshCw className="h-5 w-5 mr-2 text-blue-600" />
          Refund History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {refunds.map((refund) => (
            <div key={refund.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-gray-900">
                    Refund Processed
                  </span>
                </div>
                {getStatusBadge(refund.status)}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Amount:</span>
                  <p className="font-semibold text-green-600">£{refund.amount.toFixed(2)}</p>
                </div>
                
                <div>
                  <span className="text-gray-500">Date:</span>
                  <p className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatRefundDate(refund.created_at)}
                  </p>
                </div>
                
                <div className="md:col-span-2">
                  <span className="text-gray-500">Stripe Refund ID:</span>
                  <p className="font-mono text-xs bg-white px-2 py-1 rounded border break-all">
                    {refund.stripe_refund_id}
                  </p>
                </div>
                
                {refund.reason && (
                  <div className="md:col-span-2">
                    <span className="text-gray-500">Reason:</span>
                    <p className="capitalize">{refund.reason.replace(/_/g, ' ')}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
